import React, { useCallback, useMemo, useContext } from "react"
import clsx from "clsx"
import QuotedContact from "./QuotedContact"
import QuotedContacts from "./QuotedContacts"
import { File, Mic, Ban } from "lucide-react"
import { IconButton } from "@material-ui/core"
import { GetApp, Block } from "@material-ui/icons"
import { AuthContext } from "../../../../context/Auth/AuthContext"
import ModalImageCors from "../../../ModalImageCors"
import LocationPreview from "../../../LocationPreview"
import MarkdownWrapper from "../../../MarkdownWrapper"
import WarningIcon from "../../../../assets/viewonce.svg"
import arquivoNomeFormatar from "../../../../utils/arquivoNomeFormatar"
import {
  INFO_MESSAGE_DELETED,
  INFO_MESSAGE_RESTRICTED,
  mensagemPlaceholderVisibilidadeEh,
} from "../../../../helpers/mensagemVisibilidadeClienteAplicar"

/**
 * @typedef {Object} QuotedMessageProps
 * @property {import("../MessageItem").Message} message
 * @property {import("@material-ui/styles").ClassNameMap} classes
 */

const QuotedMessage = React.memo(
  /**
   * @param {QuotedMessageProps} props
   */
  ({ message, lEhConversaInterna, classes }) => {
    const isViewOnce = message.quotedMsg?.body === "ERR_VIEW_ONCE_MESSAGE"
    const lQuoteMascarado = mensagemPlaceholderVisibilidadeEh(message.quotedMsg?.body)

    const handleClick = useCallback(
      async (e) => {
        e.preventDefault()
        if (lQuoteMascarado) return

        const target = document.getElementById(message.quotedMsg?.id)

        if (!target) {
          console.error("Elemento da mensagem citada não encontrado.", message.quotedMsg?.id)
          return
        }

        await new Promise((resolve) => {
          target.scrollIntoView({ behavior: "smooth", block: "center" })
          setTimeout(resolve, 500)
        })

        if (target.blinkInterval) {
          clearInterval(target.blinkInterval)
          target.classList.remove("blink")
        }

        target.classList.add("blink")
        setTimeout(() => {
          target.classList.remove("blink")
        }, 1800)
      },
      [message.quotedMsg?.id, lQuoteMascarado],
    )

    const getAudioDuration = useCallback(() => {
      if (lQuoteMascarado || !message?.quotedMsg?.dataJson) {
        return "Audio"
      }

      try {
        const seconds = JSON.parse(message.quotedMsg.dataJson)?.message?.audioMessage?.seconds

        if (isNaN(seconds) || seconds == null) {
          return "Audio"
        }

        const min = Math.floor(seconds / 60)
        const sec = String(seconds % 60).padStart(2, "0")
        return `${min}:${sec}`
      } catch (e) {
        return "Audio"
      }
    }, [message?.quotedMsg?.dataJson, lQuoteMascarado])

    const shouldHideBody = useMemo(() => {
      const { quotedMsg } = message
      if (!quotedMsg) return true
      if (mensagemPlaceholderVisibilidadeEh(quotedMsg.body)) return true

      return (
        quotedMsg.body === quotedMsg.mediaUrl?.split("/").pop() ||
        quotedMsg.body === "_INFO_STICKER_INFO_" ||
        ["contactMessage", "contactsArrayMessage", "locationMessage", "liveLocationMessage"].includes(
          quotedMsg.mediaType,
        )
      )
    }, [message.quotedMsg])

    const { user } = useContext(AuthContext)

    const lMensagemPropria = lEhConversaInterna ? message.usuarioId === user.id : message.fromMe

    const cTextoMascarado =
      message.quotedMsg?.body === INFO_MESSAGE_RESTRICTED
        ? "Mensagem restrita"
        : message.quotedMsg?.body === INFO_MESSAGE_DELETED
          ? "Mensagem apagada"
          : null

    const oEstiloWrapper = { textDecoration: "none", color: "inherit", width: "100%" }

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: lMensagemPropria,
        })}
      >
        {lQuoteMascarado ? (
          <div style={oEstiloWrapper}>
            <div className={classes.quotedMsg} style={{ pointerEvents: "none" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontStyle: message.quotedMsg?.body === INFO_MESSAGE_DELETED ? "italic" : "normal",
                }}
                aria-label={cTextoMascarado}
              >
                {message.quotedMsg?.body === INFO_MESSAGE_RESTRICTED ? (
                  <Ban size={16} style={{ marginRight: 8 }} aria-hidden="true" />
                ) : (
                  <Block style={{ marginRight: 8, fontSize: 18 }} aria-hidden="true" />
                )}
                <span>{cTextoMascarado}</span>
              </div>
            </div>
          </div>
        ) : (
        <a href="#" onClick={handleClick} style={oEstiloWrapper}>
          <div className={classes.quotedMsg} style={{ pointerEvents: "none" }}>
            {!message.quotedMsg?.fromMe && (
              <span className={classes.messageContactName}>{message.quotedMsg?.contact?.name}</span>
            )}

            {isViewOnce ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src={WarningIcon} alt="Warning" style={{ marginRight: 8, height: 18, width: 18 }} />
                <span>Foto</span>
              </div>
            ) : (
              <>
                {message.quotedMsg?.mediaType === "contactMessage" && (
                  <QuotedContact message={message.quotedMsg} classes={classes} />
                )}
                {message.quotedMsg?.mediaType === "contactsArrayMessage" && (
                  <QuotedContacts message={message.quotedMsg} classes={classes} />
                )}
                {message.quotedMsg?.mediaType === "audio" && (
                  <div className={classes.audioMessage}>
                    <Mic style={{ marginLeft: 10, marginRight: 10 }} />
                    {getAudioDuration()}
                  </div>
                )}
                {message.quotedMsg?.mediaType === "video" && message.quotedMsg?.mediaUrl && (
                  <video className={classes.messageMedia} src={message.quotedMsg.mediaUrl} controls />
                )}
                {(message.quotedMsg?.mediaType === "application" || message.quotedMsg?.mediaType === "text") && (
                  <div className={classes.downloadMedia}>
                    <File style={{ marginLeft: 10, marginRight: 10 }} />
                    <span>{arquivoNomeFormatar(message?.quotedMsg?.mediaUrl, 44)}</span>
                    <IconButton variant="outlined" target="_blank" href={message?.mediaUrl}>
                      <GetApp />
                    </IconButton>
                  </div>
                )}
                {message.quotedMsg?.mediaType === "image" && message.quotedMsg?.mediaUrl && (
                  <ModalImageCors
                    imageUrl={message.quotedMsg.mediaUrl}
                    sticker={message.quotedMsg?.body === "_INFO_STICKER_INFO_"}
                  />
                )}
                {(message.quotedMsg?.mediaType === "locationMessage" ||
                  message.quotedMsg?.mediaType === "liveLocationMessage") &&
                  message.quotedMsg?.body &&
                  (() => {
                    const locationParts = message.quotedMsg.body.split("|")
                    const imageLocation = locationParts[0]
                    return <LocationPreview image={imageLocation} />
                  })()}
                <MarkdownWrapper>{shouldHideBody ? "" : message.quotedMsg?.body}</MarkdownWrapper>
              </>
            )}
          </div>
        </a>
        )}
      </div>
    )
  },
)

QuotedMessage.displayName = "QuotedMessage"

export default QuotedMessage
